import { ArrowDownOutlined, CheckCircleTwoTone } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  InputNumber,
  Row,
  Select,
  Spin,
  Statistic,
} from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import _ from 'lodash';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { customFleets } from 'shared';
import { AppStore, CarStore, ReportsDataStore, ShopStore } from 'stores';
import styled from 'styled-components';
import { RoiSamples } from './sample-data';

const { Option } = Select;

const StyledFormItem = styled(Form.Item)`
  & label {
    float: left;
  }
`;

const NoIssuesSection = styled.div`
  display: flex;
  align-items: center;
  p {
    margin-bottom: 0px;
    font-size: 14px;
  }
  i {
    font-size: 36px;
    margin-right: 12px;
  }
`;

const DownTimeCalc = observer(({ isSampleEnable }) => {
  const [form] = Form.useForm();
  const [serviceHours, setServiceHours] = useState(
    ShopStore.currentShop.id === 1065 ? 2 : 1
  );
  const [serviceCost, setServiceCost] = useState(
    ShopStore.currentShop.id === 1065 ? 750 : 70
  );
  const [chartDisplayType, setChartDisplayType] = useState('hours');

  const report = () => {
    let roiReport = ReportsDataStore.roiReport.data;

    if (isSampleEnable) {
      return {
        downtimeMonths: RoiSamples.downtimeMonths,
        downtimeHoursDefault: RoiSamples.downtimeHoursDefault,
        downtimeHoursSavedDefault: RoiSamples.downtimeHoursSavedDefault,
      };
    }

    if (_.isEmpty(roiReport)) return {};

    const indexRoiReports = _.findIndex(roiReport, (report) =>
      _.sum([
        Number(report['count']['replace']),
        Number(report['count']['repair']),
        Number(report['count']['adjust']),
        Number(report['count']['inspect']) !== 0,
      ])
    );

    if (indexRoiReports === -1) return {};

    roiReport = _.slice(roiReport, indexRoiReports);

    const downtimeMonths = _.map(roiReport, (report) =>
      moment(report['services_by_month']).utc().format('MMM-YY')
    );

    const downtimeHoursDefault = _.map(roiReport, (report) =>
      _.sum([
        Number(report['count']['replace']),
        Number(report['count']['repair']),
        Number(report['count']['adjust']),
        Number(report['count']['inspect']),
      ])
    );

    const downtimeHoursSavedDefault = _.map(roiReport, (report) =>
      _.sum([
        Number(report['count']['replace']),
        Number(report['count']['repair']),
        (Number(report['count']['adjust']) ||
          Number(report['count']['inspect'])) &&
          1,
      ])
    );

    return {
      downtimeMonths,
      downtimeHoursDefault,
      downtimeHoursSavedDefault,
    };
  };

  const getChartOptions = (yAxis, unit, data) => {
    let plotLines = [];

    if (customFleets.cardinalCourier.includes(ShopStore.currentShop.id)) {
      plotLines = [
        {
          color: '#009e73',
          width: 2,
          value: 20,
          dashStyle: 'longdashdot',
        },
      ];
    }

    return {
      title: {
        text: `Avg. Downtime ${yAxis} (${unit})`,
      },
      xAxis: {
        title: {
          text: 'Months',
        },
        categories: report.downtimeMonths,
        plotLines,
      },
      yAxis: {
        title: {
          text: yAxis,
        },
      },
      tooltip: {
        valueSuffix: unit,
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: true,
          },
          enableMouseTracking: true,
        },
      },
      series: [
        {
          type: 'line',
          name: `Actual ${yAxis === 'Cost' ? yAxis : 'Downtime'} (${unit})`,
          data: data[0],
          color: '#f92d2e',
        },
        {
          type: 'line',
          name: `Estimated ${yAxis === 'Cost' ? yAxis : 'Downtime'} with Pitstop (${unit})`,
          data: data[1],
          dashStyle: 'longdash',
          color: '#123fbf',
        },
      ],
    };
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const newServiceHours =
        values.avgHoursPerService ||
        (ShopStore.currentShop.id === 1065 ? 2 : 1);
      const newServiceCost =
        values.avgCostPerService ||
        (ShopStore.currentShop.id === 1065 ? 750 : 200);

      setServiceHours(newServiceHours);
      setServiceCost(newServiceCost);
    } catch (error) {
      AppStore.addError('Validation failed:', error.message);
    }
  };

  if (CarStore.demo === false) return <Empty />;

  if (
    CarStore.pending ||
    (CarStore.demo !== false && ReportsDataStore.roiReport.pending)
  ) {
    return <Spin />;
  }

  const currentReport = report();
  if (
    _.isEmpty(currentReport) ||
    CarStore.demo === false ||
    ShopStore.currentShop.id === 665
  ) {
    return (
      <NoIssuesSection>
        <CheckCircleTwoTone />
        <p>
          In order to view ROI, Please share Repair Order/Service History for
          your vehicles!
        </p>
      </NoIssuesSection>
    );
  }

  const { downtimeHoursDefault, downtimeHoursSavedDefault } = currentReport;

  return (
    <>
      <Row gutter={16}>
        <Col xxl={{ span: 5 }} xl={{ span: 8 }}>
          <Card>
            <Statistic
              title="Downtime Hours Saved with Pitstop"
              value={
                (_.sum(downtimeHoursDefault) -
                  _.sum(downtimeHoursSavedDefault)) *
                serviceHours
              }
              precision={2}
              valueStyle={{ color: '#3f8600' }}
              prefix={<ArrowDownOutlined />}
              suffix="Hrs"
            />
          </Card>
        </Col>
        <Col xxl={{ span: 5 }} xl={{ span: 8 }}>
          <Card>
            <Statistic
              title="Downtime Cost Saved with Pitstop"
              value={
                (_.sum(downtimeHoursDefault) -
                  _.sum(downtimeHoursSavedDefault)) *
                serviceCost
              }
              precision={2}
              valueStyle={{ color: '#3f8600' }}
              prefix={
                <>
                  <ArrowDownOutlined />$
                </>
              }
            />
          </Card>
        </Col>
      </Row>

      {chartDisplayType === 'hours' ? (
        <HighchartsReact
          highcharts={Highcharts}
          options={getChartOptions('Hours', 'Hrs', [
            downtimeHoursDefault.map((hour) => hour * serviceHours),
            downtimeHoursSavedDefault.map((hour) => hour * serviceHours),
          ])}
        />
      ) : (
        <HighchartsReact
          highcharts={Highcharts}
          options={getChartOptions('Cost', '$', [
            downtimeHoursDefault.map((hour) => hour * serviceCost),
            downtimeHoursSavedDefault.map((hour) => hour * serviceCost),
          ])}
        />
      )}

      <p>Chart type:</p>
      <Select onChange={setChartDisplayType} defaultValue="hours">
        <Option value="hours">Hours saved</Option>
        <Option value="cost">Cost saved</Option>
      </Select>

      <h4>Downtime Calculator</h4>
      <Form
        form={form}
        labelCol={{ xxl: { span: 10 }, xl: { span: 12 } }}
        wrapperCol={{ span: 12 }}
        onFinish={handleSubmit}
        style={{ display: 'flex' }}
      >
        <Col xxl={{ span: 10 }} xl={{ span: 12 }}>
          <StyledFormItem
            label="Avg. Downtime Hours Per Service"
            name="avgHoursPerService"
            extra={
              ShopStore.currentShop.id === 1065
                ? 'Default: 2 hour/ Service'
                : 'Default: 1 hour/ Service'
            }
            rules={[{ type: 'number' }]}
          >
            <InputNumber min={1} />
          </StyledFormItem>
        </Col>
        <Col xxl={{ span: 10 }} xl={{ span: 10 }}>
          <StyledFormItem
            label="Avg. Downtime Cost Per Service"
            name="avgCostPerService"
            extra={
              ShopStore.currentShop.id === 1065
                ? 'Default: $750/ Service'
                : 'Default: $200/ Service'
            }
            rules={[{ type: 'number' }]}
          >
            <InputNumber min={1} />
          </StyledFormItem>
        </Col>
        <Col xxl={{ span: 8 }} xl={{ span: 4 }}>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Calculate
            </Button>
          </Form.Item>
        </Col>
      </Form>
    </>
  );
});

DownTimeCalc.propTypes = {
  isSampleEnable: PropTypes.bool,
};

export default DownTimeCalc;
